import React from 'react';
import { useAppSelector } from 'src/utils/Hooks';
import { SearchSelect } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/components/SearchSelect';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import { getFieldConfig } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants/formConfigs';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

/**
 * BrandSelectForBrandsStep component for brand selection on the Brands step.
 * This is used when brandGroupings is hidden and brandIds needs to be selected here.
 */
export const BrandSelectForBrandsStep = () => {
  const {
    selections: { control },
    currentFormConfig
  } = usePresentationFormContext();

  // Get brands from Redux store
  const brandsFollowing = useAppSelector((state) => state.brandsFollowing) as Array<{
    id?: number;
    brandId?: number;
    name?: string;
    brandName?: string;
    displayName?: string;
  }>;

  // Get field configuration
  const brandConfig = getFieldConfig(currentFormConfig, 'brandIds');

  // Format brands for the select component
  const selectOptions = brandsFollowing
    .filter((brand) => brand.id !== 0) // Filter out "all brands" option
    .map((brand) => {
      const id = String(brand.id ?? brand.brandId ?? '');
      const displayName = brand.brandName ?? brand.displayName ?? brand.name ?? 'Unnamed Brand';
      return {
        id,
        value: id,
        displayName
      };
    })
    .filter((brand) => brand.id !== '' && brand.id !== '0');

  // Build dynamic placeholder based on config
  const getPlaceholder = () => {
    if (brandConfig.minCount !== undefined && brandConfig.maxCount !== undefined) {
      if (brandConfig.minCount === brandConfig.maxCount) {
        return `Select exactly ${brandConfig.minCount} brand${brandConfig.minCount > 1 ? 's' : ''}`;
      }
      return `Select ${brandConfig.minCount} to ${brandConfig.maxCount} brands`;
    }
    return 'Search or select brands you would like to include in your presentation';
  };

  return (
    <Flex flexDirection="column" gap={16}>
      <Flex flexDirection="column" gap={8}>
        <Text variant="h4" lineHeight="28px">
          Brand Selection
        </Text>
        <Text variant="body1" sx={{ color: 'text.secondary' }}>
          Select the brand(s) to include in your presentation.
        </Text>
      </Flex>
      
      <SearchSelect
        name="brandIds"
        title="Brands"
        placeholder={getPlaceholder()}
        selectOptions={selectOptions}
        isMulti={true}
        maxCount={brandConfig.maxCount}
        control={control}
      />
    </Flex>
  );
};
